<template>
  <ActionPanelShell title="Start a follow-up" :subtitle="subtitle" @close="close">
    <div class="sp" data-test="followup-panel">
      <!-- Nothing can be started: say so, and why, instead of a form full of greyed-out controls. -->
      <section v-if="blocked" class="sp-sec" data-test="followup-start-blocked">
        <PanelNotice tone="warn" icon="fa-exclamation-triangle">
          <strong class="fp-blocked__t">Nothing can be started right now</strong>
          <span class="fp-blocked__d">{{ blockedSentence }}</span>
          <button
            v-if="canSeeOpen"
            type="button"
            class="sp-link fp-blocked__see"
            data-test="followup-see-open"
            @click="seeOpen"
          >See open follow-ups</button>
        </PanelNotice>
      </section>

      <section class="sp-sec" data-test="followup-types">
        <div class="sp-h">What are you following up on?</div>
        <label
          v-for="t in types"
          :key="t.value"
          class="fp-type"
          :class="{ 'is-on': typeID === t.value && !t.off, 'is-off': t.off }"
        >
          <input
            v-model="typeID"
            type="radio"
            name="followup-start-type"
            class="fp-type__radio"
            :value="t.value"
            :disabled="t.off"
            :data-test="`followup-start-type-${t.value}`"
          >
          <span class="fp-type__text">
            <span class="fp-type__name">{{ t.label }}</span>
            <span class="fp-type__desc">{{ t.desc }}</span>
            <span v-if="t.off" class="fp-type__why" :data-test="`followup-start-why-${t.value}`">
              <i class="fa fa-lock" aria-hidden="true"></i> {{ t.why }}
            </span>
          </span>
        </label>
      </section>

      <section class="sp-sec" data-test="followup-when">
        <label class="sp-h" for="followup-start-date">Start following up on</label>
        <input
          id="followup-start-date"
          v-model="date"
          type="date"
          class="sp-input"
          :disabled="blocked"
          data-test="followup-start-date"
        >
        <div class="fp-quick" role="group" aria-label="Quick dates">
          <button
            v-for="q in quick"
            :key="q.label"
            type="button"
            class="fp-chip"
            :class="{ 'is-on': date === q.ymd && !blocked }"
            :disabled="blocked"
            :data-test="`followup-quick-${q.n}`"
            @click="date = q.ymd"
          >{{ q.label }}</button>
        </div>
        <p class="sp-note" data-test="followup-date-hint">{{ dateHint }}</p>
      </section>

      <section class="sp-sec" data-test="followup-note-sec">
        <div class="sp-head">
          <label class="sp-h" for="followup-start-note">Note</label>
          <span class="sp-sub fp-optional">Optional</span>
        </div>
        <textarea
          id="followup-start-note"
          v-model="note"
          rows="4"
          class="fp-textarea"
          :placeholder="notePlaceholder"
          :disabled="blocked"
          data-test="followup-start-note"
        ></textarea>
        <PanelNotice v-if="saveError" tone="error" dismissible data-test="followup-error" @dismiss="saveError = ''">{{ saveError }}</PanelNotice>
      </section>
    </div>

    <template #footer>
      <span class="ap-foot-note" data-test="followup-foot-note">{{ footNote }}</span>
      <button
        type="button"
        class="fp-foot-btn ap-foot-main"
        :disabled="!canStart"
        data-test="followup-start-save"
        @click="save"
      >{{ saving ? 'Starting…' : 'Start follow-up' }}</button>
    </template>
  </ActionPanelShell>
</template>

<script>
/**
 * "Start a follow-up" — the redesigned panel (design "Patient Action Panels" 2b), replacing the
 * small FollowupStartModal the new Patient Profile page and the Schedule View patient panel used.
 * Opened by PatientPanelActionHost.run('followup') through openActionPanel() (480px, 'ap-slideout').
 *
 * The RULES and the PAYLOAD are unchanged — the pure src/utils/patientPanel/followupStart.js, which
 * patient-profile-adapter.test.js holds to the classic "Initiate a Follow-up" dropdown key for key —
 * and so is the save: `tasks/updateInitialFollowupDate`. New: each type says what it covers, a type
 * that cannot be started says WHY (followupStartBlockedReasons) instead of greying out, quick date
 * chips, and one main button in the footer that says exactly what it will do.
 *
 * Callbacks arrive as PROPS (vue2-slideout-panel binds only `closePanel`): `onSaved` reloads the
 * host page's patient; `onSeeFollowups` takes the page to its Follow-ups tab.
 *
 * Native <input type="date"> (house rule). Text fields take their 14px / 16px-on-touch size from
 * the ap-* mixins (iOS focus-zoom).
 *
 * ⛔ No mixin.js, and no prop named `patNum`, `siteID`, `loading` or `allSettings`.
 */
import moment from 'moment-timezone';
import ActionPanelShell from './ActionPanelShell.vue';
import PanelNotice from './PanelNotice.vue';
import {
  FOLLOWUP_START_TYPES, followupStartOptions, followupStartBlockedReasons, buildFollowupStartPayload, addDaysToYmd,
} from '@/utils/patientPanel/followupStart';

const LABELS = { 4: 'Unscheduled treatment', 3: 'Recall' };
const QUICK = [['Today', 0], ['Tomorrow', 1], ['In 1 week', 7], ['In 2 weeks', 14], ['In 1 month', 30]];

export default {
  name: 'FollowupPanel',

  components: { ActionPanelShell, PanelNotice },

  props: {
    siteId: { type: String, required: true },
    patientNum: { type: [String, Number], required: true },
    /** '<siteID>-<PatNum>' */
    teamioPatientId: { type: String, required: true },
    /** The patient row (FName, LName, RecallScheduled…). */
    patient: { type: Object, default: null },
    followups: { type: Array, default: () => [] },
    unscheduledTx: { type: Array, default: () => [] },
    /** belongsToLists rows. */
    lists: { type: Array, default: () => [] },
    /** YYYY-MM-DD in the site's timezone. */
    today: { type: String, required: true },
    notePlaceholder: { type: String, default: 'Anything the next person should know' },
    /** Called after a follow-up is saved (the host page reloads the patient). */
    onSaved: { type: Function, default: null },
    /** Called by "See open follow-ups" (the host page opens its Follow-ups tab). */
    onSeeFollowups: { type: Function, default: null },
  },

  data() {
    return { typeID: null, date: this.today, note: '', saving: false, saveError: '' };
  },

  computed: {
    firstName() {
      return (this.patient && this.patient.FName) || 'This patient';
    },
    subtitle() {
      const p = this.patient || {};
      const name = [p.FName, p.LName].filter(Boolean).join(' ').trim();
      return name ? `For ${name}` : '';
    },
    context() {
      return { followups: this.followups, unscheduledTx: this.unscheduledTx, patient: this.patient };
    },
    options() {
      return followupStartOptions(this.context);
    },
    reasons() {
      return followupStartBlockedReasons(this.context);
    },
    blocked() {
      return !this.options.any;
    },

    /** Each type says what it covers; a blocked one says why. */
    types() {
      return FOLLOWUP_START_TYPES.map(({ value }) => ({
        value,
        label: LABELS[value],
        desc: this.describe(value),
        off: !this.options[value],
        why: this.reasons[value],
      }));
    },

    blockedSentence() {
      const open = 'Already has an open follow-up';
      if (this.reasons[4] === open && this.reasons[3] === open) {
        return `Both types already have an open follow-up for ${this.firstName}. Close one before starting another.`;
      }
      return `${LABELS[4]}: ${this.reasons[4].toLowerCase()}. ${LABELS[3]}: ${this.reasons[3].toLowerCase()}.`;
    },
    canSeeOpen() {
      return typeof this.onSeeFollowups === 'function' && (this.followups || []).length > 0;
    },

    quick() {
      return QUICK.map(([label, n]) => ({ label, n, ymd: addDaysToYmd(this.today, n) }));
    },
    dateHint() {
      if (!this.date) return 'Pick the day it should show on the Follow-ups list.';
      if (this.date === this.today) return 'Shows on the Follow-ups list today.';
      const m = moment(this.date, 'YYYY-MM-DD', true);
      return m.isValid() ? `Shows on the Follow-ups list on ${m.format('MMM D, YYYY')}.` : '';
    },

    canStart() {
      return !this.saving && !this.blocked && !!this.typeID && !!this.options[this.typeID] && !!this.date;
    },
    footNote() {
      if (this.blocked) return 'Nothing to start';
      if (!this.typeID) return 'Pick what you’re following up on';
      return '';
    },
  },

  created() {
    // The classic dropdown starts on today with nothing picked — except when only one type is open.
    this.typeID = this.options[4] && !this.options[3] ? 4 : (this.options[3] && !this.options[4] ? 3 : null);
  },

  methods: {
    describe(value) {
      if (value === 4) {
        const n = (this.unscheduledTx || []).length;
        return n > 0 ? `${n} procedure${n === 1 ? '' : 's'} planned, none scheduled yet.` : 'Treatment that is planned but not scheduled yet.';
      }
      return `${this.firstName}’s recall isn’t scheduled.`;
    },

    close() {
      this.$emit('closePanel', {});
    },

    seeOpen() {
      if (this.onSeeFollowups) this.onSeeFollowups();
      this.close();
    },

    async save() {
      if (!this.canStart) return;
      const customer = this.$store.state.customer || {};
      const { payload, error } = buildFollowupStartPayload({
        typeID: this.typeID,
        followupDate: this.date,
        today: this.today,
        note: this.note,
        siteId: this.siteId,
        patientNum: this.patientNum,
        teamioPatientID: this.teamioPatientId,
        userID: customer.userID,
        user: customer.user || {},
        lists: this.lists,
      });
      if (error) {
        if (this.$toast) this.$toast.error(error);
        return;
      }
      this.saving = true;
      this.saveError = '';
      try {
        await this.$store.dispatch('tasks/updateInitialFollowupDate', payload);
        if (this.$toast) this.$toast.success('Your followup has been created');
        if (this.onSaved) this.onSaved();
        this.close();
      } catch (err) {
        console.error('[FollowupPanel] save error:', err);
        this.saveError = 'Could not start the follow-up. Please try again.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;

.fp-foot-btn { @include ap-btn-primary; }

.fp-blocked__t { display: block; font-size: 14px; }
.fp-blocked__d { display: block; margin-top: 3px; }
.fp-blocked__see { display: inline-block; margin-top: 8px; }

.fp-optional { margin: 0; }

// The two types: a card each, the whole card is the tap target (48px+ on touch).
.fp-type {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 12px 14px;
  border: 1.5px solid $pp-line;
  border-radius: $pp-radius-lg;
  background: $pp-surface;
  cursor: pointer;

  @include ap-touch { min-height: $ap-row-h-touch; }

  &.is-on { border-color: $pp-brand-deep; background: $pp-brand-soft; }

  &.is-off {
    border-color: $pp-line;
    background: $pp-bg-2;
    cursor: not-allowed;
  }
}

.fp-type__radio {
  flex: none;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: $pp-brand-deep;
  cursor: inherit;
}

.fp-type__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.fp-type__name { font-size: 14.5px; font-weight: 700; color: $pp-ink; }
.fp-type__desc { font-size: 13px; line-height: 1.4; color: $pp-ink-3; }
.fp-type.is-off .fp-type__name { color: $pp-ink-3; }

.fp-type__why {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: $pp-amber-deep;
}

// Quick dates.
.fp-quick { display: flex; flex-wrap: wrap; gap: 6px; }

.fp-chip {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border: 1px solid $pp-line;
  border-radius: $pp-radius-pill;
  background: $pp-surface;
  color: $pp-ink-2;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { background: $pp-bg; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; }
  &:disabled { color: $pp-ink-4; cursor: not-allowed; }

  &.is-on { border-color: $pp-brand-deep; background: $pp-brand-soft; color: $pp-brand-deep; }

  @include ap-touch { height: 40px; padding: 0 14px; }
}

.fp-textarea { @include ap-textarea; }
</style>
